import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

export interface DragState {
  isDragging: boolean;
  dragType: string | null;
  dragData: unknown;
}

export interface LayoutContextValue {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: Dispatch<SetStateAction<boolean>>;
  panelSizes: Record<string, number>;
  setPanelSizes: Dispatch<SetStateAction<Record<string, number>>>;
  keyboardShortcutsEnabled: boolean;
  setKeyboardShortcutsEnabled: Dispatch<SetStateAction<boolean>>;
  commandPaletteOpen: boolean;
  setCommandPaletteOpen: Dispatch<SetStateAction<boolean>>;
  globalSearchQuery: string;
  setGlobalSearchQuery: Dispatch<SetStateAction<string>>;
  globalSearchResults: unknown[];
  setGlobalSearchResults: Dispatch<SetStateAction<unknown[]>>;
  dragState: DragState;
  setDragState: Dispatch<SetStateAction<DragState>>;
  focusedElement: string | null;
  setFocusedElement: Dispatch<SetStateAction<string | null>>;
}

const LS_KEY_SIDEBAR = "ive-sidebar-collapsed";
const LS_KEY_PANEL_SIZES = "ive-panel-sizes";
const LS_KEY_SHORTCUTS = "ive-shortcuts-enabled";

const INITIAL_DRAG_STATE: DragState = {
  isDragging: false,
  dragType: null,
  dragData: null,
};

const LayoutContext = createContext<LayoutContextValue | null>(null);

function readBoolean(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed === "boolean") return parsed;
  throw new Error(
    `Invalid boolean in localStorage key "${key}": ${String(parsed)}`,
  );
}

function readPanelSizes(): Record<string, number> {
  if (typeof window === "undefined") return {};
  const raw = localStorage.getItem(LS_KEY_PANEL_SIZES);
  if (raw === null) return {};
  const parsed: unknown = JSON.parse(raw);
  if (
    typeof parsed === "object" &&
    parsed !== null &&
    !Array.isArray(parsed) &&
    Object.values(parsed as Record<string, unknown>).every(
      (v) => typeof v === "number",
    )
  ) {
    return parsed as Record<string, number>;
  }
  throw new Error(
    `Invalid panel sizes in localStorage key "${LS_KEY_PANEL_SIZES}": ${String(raw)}`,
  );
}

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() =>
    readBoolean(LS_KEY_SIDEBAR, false),
  );
  const [panelSizes, setPanelSizes] = useState<Record<string, number>>(
    readPanelSizes,
  );
  const [keyboardShortcutsEnabled, setKeyboardShortcutsEnabled] =
    useState<boolean>(() => readBoolean(LS_KEY_SHORTCUTS, true));

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState<unknown[]>([]);
  const [dragState, setDragState] = useState<DragState>(INITIAL_DRAG_STATE);
  const [focusedElement, setFocusedElement] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(LS_KEY_SIDEBAR, JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem(LS_KEY_PANEL_SIZES, JSON.stringify(panelSizes));
  }, [panelSizes]);

  useEffect(() => {
    localStorage.setItem(
      LS_KEY_SHORTCUTS,
      JSON.stringify(keyboardShortcutsEnabled),
    );
  }, [keyboardShortcutsEnabled]);

  const value = useMemo<LayoutContextValue>(
    () => ({
      sidebarCollapsed,
      setSidebarCollapsed,
      panelSizes,
      setPanelSizes,
      keyboardShortcutsEnabled,
      setKeyboardShortcutsEnabled,
      commandPaletteOpen,
      setCommandPaletteOpen,
      globalSearchQuery,
      setGlobalSearchQuery,
      globalSearchResults,
      setGlobalSearchResults,
      dragState,
      setDragState,
      focusedElement,
      setFocusedElement,
    }),
    [
      sidebarCollapsed,
      panelSizes,
      keyboardShortcutsEnabled,
      commandPaletteOpen,
      globalSearchQuery,
      globalSearchResults,
      dragState,
      focusedElement,
    ],
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}

export function useLayoutContext(): LayoutContextValue {
  const ctx = useContext(LayoutContext);
  if (ctx === null) {
    throw new Error("useLayoutContext must be used within a LayoutProvider");
  }
  return ctx;
}
